import type { EventHint, ErrorEvent as SentryErrorEvent } from '@sentry/react';

import { readCookie } from 'support/helpers/cookies/cookies';

import {
  buildAutomationFingerprint,
  computeVirginStorageAtBoot,
  createEmptyMutationWindow,
  createEmptyTranslationFingerprints,
  detectTranslationFingerprints,
  DomInterferenceSnapshot,
  IMMERSIVE_TRANSLATE_MARKER_ATTRIBUTES,
  isDomInterferenceSuspected,
  LastUiActionSnapshot,
  MS_TRANSLATE_ATTRIBUTES,
  MutationWindow,
  pruneMutationWindow,
  recordMutations,
  safely,
  shouldAttachDomInterferenceContext,
  summarizeMutationWindow,
  TranslationFingerprints,
} from './helpers';

const REACT_MOUNT_ELEMENT_ID = 'root';
const UI_ACTION_ATTRIBUTE = 'data-cheese-id';
const OBSERVED_ATTRIBUTES = [
  'lang',
  'translate',
  'class',
  ...MS_TRANSLATE_ATTRIBUTES,
  ...IMMERSIVE_TRANSLATE_MARKER_ATTRIBUTES,
];

let started = false;
let mutationWindow: MutationWindow = createEmptyMutationWindow();
let translationFingerprints: TranslationFingerprints = createEmptyTranslationFingerprints();
let lastUiAction: Omit<LastUiActionSnapshot, 'ageMs'> | null = null;
let htmlLangAtInit: string | null = null;
let virginStorageAtBoot: boolean | null = null;

export const startDomInterferenceMonitor = () => {
  if (started || typeof MutationObserver === 'undefined') {
    return;
  }

  try {
    started = true;
    virginStorageAtBoot = computeVirginStorageAtBoot();
    htmlLangAtInit = document.documentElement.getAttribute('lang');

    const observer = new MutationObserver(handleMutations);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: OBSERVED_ATTRIBUTES,
    });

    document.addEventListener('click', handleUiActionClick, { capture: true });
  } catch {
    // A telemetry bug must never break the app.
  }
};

export const getSnapshot = (): DomInterferenceSnapshot => {
  const nowMs = Date.now();

  mutationWindow = pruneMutationWindow(mutationWindow, Math.floor(nowMs / 1000));

  return {
    ...summarizeMutationWindow(mutationWindow, nowMs),
    ...buildAutomationFingerprint(virginStorageAtBoot),
    translationFingerprints,
    environment: {
      navigatorLanguage: safely(() => navigator.language ?? null, null),
      navigatorLanguages: safely(() => Array.from(navigator.languages ?? []), []),
      htmlLangAtInit,
      htmlLangAtSnapshot: safely(() => document.documentElement.getAttribute('lang'), null),
      htmlTranslateAttribute: safely(() => document.documentElement.getAttribute('translate'), null),
    },
    lastUiAction: lastUiAction ? { ...lastUiAction, ageMs: nowMs - lastUiAction.timestamp } : null,
  };
};

export const domInterferenceBeforeSend = (event: SentryErrorEvent, _hint: EventHint): SentryErrorEvent => {
  const taggedEvent: SentryErrorEvent = {
    ...event,
    tags: { ...event.tags, authenticated: readAuthenticatedTag() },
  };

  try {
    if (!shouldAttachDomInterferenceContext(event)) {
      return taggedEvent;
    }

    const snapshot = getSnapshot();
    const suspected = isDomInterferenceSuspected(snapshot);

    return {
      ...taggedEvent,
      contexts: {
        ...taggedEvent.contexts,
        dom_interference: { ...snapshot },
      },
      tags: {
        ...taggedEvent.tags,
        dom_interference_suspected: suspected ? 'true' : 'false',
      },
    };
  } catch {
    return taggedEvent;
  }
};

const handleMutations = (records: Array<MutationRecord>) => {
  try {
    const rootNode = document.getElementById(REACT_MOUNT_ELEMENT_ID);

    mutationWindow = recordMutations(mutationWindow, records, rootNode, Date.now());
    translationFingerprints = detectTranslationFingerprints(records, translationFingerprints, {
      htmlElement: document.documentElement,
      htmlLangAtInit,
    });
  } catch {
    // A telemetry bug must never break the app.
  }
};

const handleUiActionClick = (event: MouseEvent) => {
  try {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const trigger = target.closest(`[${UI_ACTION_ATTRIBUTE}]`);
    if (!trigger) {
      return;
    }

    lastUiAction = {
      analyticsId: trigger.getAttribute(UI_ACTION_ATTRIBUTE) ?? '',
      pathname: window.location.pathname,
      timestamp: Date.now(),
    };
  } catch {
    // A telemetry bug must never break the app.
  }
};

const readAuthenticatedTag = (): 'true' | 'false' =>
  safely(() => (readCookie('XSRF-TOKEN') !== null ? 'true' : 'false'), 'false');
